import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { isField } from "@fobu/schema";
import { useFormEnv } from "../lib/context";
import { t } from "../lib/i18n";
import { plain } from "../lib/markdown";
import { fieldDomId } from "./FieldShell";

/** Fokus auf das erste bedienbare Element eines Feldes. */
export function focusField(fieldId: string) {
  const wrap = document.getElementById(`${fieldDomId(fieldId)}-wrap`);
  const target =
    (document.getElementById(fieldDomId(fieldId)) as HTMLElement | null) ??
    (wrap?.querySelector("input:not([disabled]), select, textarea, button") as HTMLElement | null) ??
    wrap;
  target?.focus({ preventScroll: false });
  wrap?.scrollIntoView?.({ block: "center", behavior: globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
}

/** Fehlerübersicht (WCAG 3.3.1/3.3.3): erscheint oben, bekommt Fokus, verlinkt jedes Feld. */
/** Fehlerübersicht: Felder mit Problemen, verlinkt. */
export function ErrorSummary({ fieldIds }: { fieldIds: string[] }) {
  const { ctl, tpl } = useFormEnv();
  const ref = useRef<HTMLDivElement>(null);
  const items = fieldIds.filter((id) => ctl.errors[id]);
  const visible = ctl.summaryVisible && items.length > 0;
  // Fokus nur nach einem Absende-Versuch – nie während der Eingabe (sonst geht Text verloren).
  useLayoutEffect(() => {
    if (visible) ref.current?.focus();
  }, [ctl.attempt]);
  if (!visible) return null;
  return (
    <div class="fb-summary" ref={ref} tabIndex={-1} role="alert" aria-labelledby="fb-summary-title">
      <p id="fb-summary-title" class="fb-summary-title">
        {t.errorSummaryTitle(items.length)}
      </p>
      <ul>
        {items.map((id) => {
          const field = ctl.idx.fieldById.get(id)!;
          return (
            <li key={id}>
              <a
                href={`#${fieldDomId(id)}`}
                onClick={(e) => {
                  e.preventDefault();
                  focusField(id);
                }}
              >
                {plain(tpl(field.label))}
                {/* „Pflichtfeld fehlt“ erklärt sich durch den Listentitel; andere Fehler werden genannt. */}
                {ctl.errors[id]![0]!.code === "required" || ctl.errors[id]![0]!.code === "consent_required" ? "" : ` – ${ctl.errors[id]![0]!.message}`}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function DraftNotice() {
  const { ctl } = useFormEnv();
  if (!ctl.draftRestored) return null;
  return (
    <div class="fb-notice" role="status">
      <p>
        {ctl.draftSavedAt ? t.draftRestoredAt(dateFmt().format(new Date(ctl.draftSavedAt))) : t.draftRestored}
        {ctl.draftChanged ? ` ${t.draftChanged}` : ""}
      </p>
      <div class="fb-notice-actions">
        <button type="button" class="fb-linkbtn" onClick={ctl.discardDraft}>
          {t.draftDiscard}
        </button>
        <button type="button" class="fb-iconbtn" onClick={ctl.dismissDraftNotice} aria-label="Hinweis schließen">
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
        </button>
      </div>
    </div>
  );
}

const dateFmt = () => new Intl.DateTimeFormat(document.documentElement.lang || "de-DE", { dateStyle: "long", timeStyle: "short" });

/** Antwort kam per Klick aus der Einladungsmail – gespeichert wird erst mit dem Absenden. */
export function MailNotice() {
  const { ctl } = useFormEnv();
  if (!ctl.fromMail) return null;
  return (
    <div class="fb-notice" role="status">
      <p>{ctl.fromMail.here ? t.fromMailHere : t.fromMailBack}</p>
    </div>
  );
}

/** Hinweis nach dem Öffnen eines Fortsetzungslinks. */
export function ResumeNotice() {
  const { ctl } = useFormEnv();
  if (!ctl.resumedAt) return null;
  return (
    <div class="fb-notice" role="status">
      <p>{t.resumeRestored(dateFmt().format(new Date(ctl.resumedAt)))}</p>
      <div class="fb-notice-actions">
        <button type="button" class="fb-iconbtn" onClick={ctl.dismissResumeNotice} aria-label={t.close}>
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
        </button>
      </div>
    </div>
  );
}

/** „Später weitermachen“: persönlicher Link (optional per E-Mail) für ein anderes Gerät. */
export function SaveForLater() {
  const { ctl } = useFormEnv();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ url: string; until: string; emailed: boolean } | null>(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  if (!ctl.canSaveForLater || ctl.status !== "filling") return null;

  const save = async () => {
    setBusy(true);
    setError(false);
    try {
      const link = await ctl.saveForLater(email.trim() || undefined);
      setResult({ url: link.url, until: dateFmt().format(new Date(link.expiresAt)), emailed: link.emailed });
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        class="fb-linkbtn fb-save-later"
        aria-expanded="false"
        onClick={() => {
          setOpen(true);
          setResult(null);
          setTimeout(() => panel.current?.querySelector<HTMLElement>("input, button")?.focus(), 0);
        }}
      >
        {t.saveForLater}
      </button>
    );
  }
  return (
    <div class="fb-banner fb-save-later-panel" ref={panel} role="group" aria-labelledby="fb-save-later-title">
      <p id="fb-save-later-title" class="fb-save-later-title">
        {t.saveForLater}
      </p>
      {!result ? (
        <>
          <p class="fb-hint">{t.saveForLaterHint}</p>
          <label class="fb-label" for="fb-save-later-email">
            {t.saveForLaterEmail}
          </label>
          <input id="fb-save-later-email" class="fb-input" type="email" autoComplete="email" value={email} aria-describedby="fb-save-later-email-hint" onInput={(e) => setEmail(e.currentTarget.value)} />
          <p id="fb-save-later-email-hint" class="fb-hint">
            {t.saveForLaterEmailHint}
          </p>
          {error && (
            <p class="fb-error" role="alert">
              {t.saveForLaterFailed}
            </p>
          )}
          <div class="fb-nav">
            <button type="button" class="fb-btn fb-btn--ghost" onClick={() => setOpen(false)}>
              {t.close}
            </button>
            <button type="button" class="fb-btn fb-btn--primary" disabled={busy} onClick={() => void save()}>
              {t.saveForLaterCreate}
            </button>
          </div>
        </>
      ) : (
        <div role="status">
          <p>{t.saveForLaterDone(result.until)}</p>
          {result.emailed && <p>{t.saveForLaterEmailed}</p>}
          <p class="fb-save-later-url">
            <a href={result.url}>{result.url}</a>
          </p>
          <div class="fb-nav">
            <button type="button" class="fb-btn fb-btn--ghost" onClick={() => setOpen(false)}>
              {t.close}
            </button>
            <button type="button" class="fb-btn fb-btn--primary" onClick={() => void navigator.clipboard?.writeText(result.url).then(() => setCopied(true))}>
              {copied ? t.copied : t.copyLink}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SubmitError() {
  const { ctl } = useFormEnv();
  if (!ctl.submitError) return null;
  return (
    <p class="fb-alert" role="alert">
      {ctl.submitError === "rate_limited" ? t.rateLimited : ctl.submitError === "closed" ? t.formClosedNow : ctl.submitError === "invite_used" ? t.inviteUsedTitle : ctl.submitError === "conflict" ? t.manageConflict : t.submitFailed}
    </p>
  );
}

export function Branding() {
  const { ctl } = useFormEnv();
  if (ctl.def.settings.showBranding === false) return null;
  return (
    <footer class="fb-footer">
      <span>{t.poweredBy}</span>
    </footer>
  );
}

export function Logo() {
  const { ctl } = useFormEnv();
  const url = ctl.def.theme.logoUrl;
  return url ? <img class="fb-logo" src={url} alt="" /> : null;
}

export function useFieldIds(blockIds: string[]) {
  const { ctl } = useFormEnv();
  return blockIds.filter((id) => {
    const b = ctl.idx.blockById.get(id)?.block;
    return b && isField(b);
  });
}

/**
 * Spam-Falle: für Menschen unsichtbar und nicht erreichbar (kein Tab-Stopp, vor Screenreadern verborgen),
 * Bots füllen sie trotzdem aus.
 */
export function Honeypot({ onInput }: { onInput: (v: string) => void }) {
  return (
    <div class="fb-hp" aria-hidden="true">
      <label>
        Website
        <input type="text" name="website" tabIndex={-1} autoComplete="off" onInput={(e) => onInput(e.currentTarget.value)} />
      </label>
    </div>
  );
}

/** Geschätzte Ausfülldauer (grob, je Feldtyp) – nur ab sechs Fragen. */
export function Duration() {
  const { ctl } = useFormEnv();
  const fields = ctl.idx.fields;
  if (!ctl.def.settings.showDuration || fields.length < 6) return null;
  const seconds = fields.reduce((sum, f) => {
    const s =
      f.type === "long_text" ? 45 : f.type === "group" ? 60 : f.type === "matrix" || f.type === "file_upload" ? 30 : f.type === "signature" ? 20 : f.type === "ranking" ? 20 : "choices" in f || f.type === "yes_no" || f.type === "consent" ? 8 : 12;
    return sum + s;
  }, 0);
  return <p class="fb-duration">{t.duration(Math.max(1, Math.round(seconds / 60)))}</p>;
}

/** Nachladen auf Abruf (Größenbudget): Live-Ergebnisse und Terminumfrage. */
function lazy<P extends object>(load: () => Promise<(p: P) => preact.JSX.Element | null>) {
  let C: ((p: P) => preact.JSX.Element | null) | null = null;
  const get = () => load().then((x) => (C = x));
  const View = (p: P) => {
    const [V, setV] = useState(() => C);
    useEffect(() => {
      if (!V) void get().then((x) => setV(() => x));
    }, []);
    return V ? <V {...p} /> : <div class="fb-media-wait" aria-busy="true" />;
  };
  /** Vorab laden (Tests): danach zeichnet die Ansicht sofort. */
  View.preload = get;
  return View;
}
export const LazyLive = lazy<{ token?: string; load?: () => Promise<unknown>; lang?: string; standalone?: boolean; heading?: boolean }>(() => import("./LiveResults").then((m) => m.LiveResults));
/** „Sofort werten“ aus der Einladungsmail – eigener kleiner Teil, nur bei Klicks aus der Mail. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyMailAnswer = lazy<any>(() => import("./MailAnswer").then((m) => m.MailAnswer));
/** Gesprächsmodus (eine Frage pro Ansicht) nur für Formulare, die ihn nutzen. */
export const LazyConversational = lazy<Record<string, never>>(() => import("./ConversationalView").then((m) => m.ConversationalView));
// Seltene Feldtypen: Code erst laden, wenn das Formular sie enthält.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyPoll = lazy<any>(() => import("./fields/PollField").then((m) => m.PollField));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyAddress = lazy<any>(() => import("./fields/CompositeFields").then((m) => m.AddressField));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyDateRange = lazy<any>(() => import("./fields/CompositeFields").then((m) => m.DateRangeField));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyAmount = lazy<any>(() => import("./fields/MoneyFields").then((m) => m.AmountField));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyOrder = lazy<any>(() => import("./fields/MoneyFields").then((m) => m.OrderField));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyEmailVerify = lazy<any>(() => import("./fields/EmailVerify").then((m) => m.EmailVerifyField));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazySlots = lazy<any>(() => import("./fields/SlotPicker").then((m) => m.SlotPicker));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyLiveParticipant = lazy<any>(() => import("./LiveParticipant").then((m) => m.LiveParticipant));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazySepa = lazy<any>(() => import("./fields/SepaField").then((m) => m.SepaField));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyRanking = lazy<any>(() => import("./fields/RankingField").then((m) => m.Ranking));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyLookup = lazy<any>(() => import("./fields/LookupField").then((m) => m.Lookup));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyFileUpload = lazy<any>(() => import("./fields/FileFields").then((m) => m.FileUpload));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazySignature = lazy<any>(() => import("./fields/FileFields").then((m) => m.Signature));

/** Vor dem Absenden: Hinweis, dass die Ergebnisse (ggf. mit Namen) für andere sichtbar sind. */
export function ResultsNotice() {
  const { ctl } = useFormEnv();
  const mode = ctl.def.settings.publicResults.mode;
  if (mode === "off") return null;
  return <p class="fb-hint fb-results-notice">{mode === "detailed" ? t.resultsNoticeDetailed : t.resultsNoticeSummary}</p>;
}

/** „Bisherige Antworten ansehen“ – nur wenn schon vor dem Abstimmen freigegeben. */
export function ResultsTeaser() {
  const { ctl, client, versionId } = useFormEnv();
  const [open, setOpen] = useState(false);
  const pr = ctl.def.settings.publicResults;
  if (pr.mode === "off" || !pr.beforeSubmit || !client.liveResults) return null;
  return (
    <div class="fb-live-toggle">
      <button type="button" class="fb-linkbtn" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? t.resultsHide : t.resultsShow}
      </button>
      {open && <LazyLive load={() => client.liveResults!(versionId, ctl.def.settings.language)} lang={ctl.def.settings.language} />}
    </div>
  );
}
